import React from "react";
import { useTranslation } from "react-i18next";
import { ContentAlignment, Link } from "fr-ito-web-react/react/esm";
import { Caption, Image } from "@common/DynamicUikit";
import Toggle from "@common/Toggle";
import styles from "./styles.module.css";

type Props = {
  /**
   * @en Style user name
   */
  userName: string;
  /**
   * @en Height of the model
   */
  modelHeight?: string;
  /**
   * @en User image url
   */
  profileImageUrl?: string;
  /**
   * @en User image alt
   */
  profileImageAlt?: string;
  /**
   * Value for wish count
   */
  count?: string | undefined;
  /**
   * To determine whether the wish button is displayed
   */
  showWish: boolean;
  /**
   * To determine whether the wish button is toggled
   */
  toggled: boolean;
  /**
   * @en transit to the personal page
   */
  personalPageLink?: string;
  /**
   * Callback function for wish button click
   */
  handleWishClick: (e: any) => void;
  /**
   * Callback function for user click
   */
  handleUserClick?: (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent> &
      React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => void;
  /**
   * @en To determine whether the badge should be rendered with the user name
   */
  isBadge?: boolean;
  /**
   * @en Store information of user
   */
  storeInfo?: string;
  /**
   * @en To transition to page of the store page
   */
  storeLink?: string;
};

const UserBar = (props: Props) => {
  const {
    userName,
    modelHeight,
    profileImageUrl,
    profileImageAlt,
    toggled,
    count,
    showWish,
    handleWishClick,
    personalPageLink,
    handleUserClick
  } = props;
  const { t } = useTranslation();

  const instaImage = (
    <Image
      alt="Instagram image"
      ratio="1x1"
      src="https://asset.uniqlo.com/g/pictures/instagram-gradient.png"
      noImage={{ imageSize: "smallest", imageType: "user" }}
    />
  );

  const profileImage = (
    <Image
      alt={profileImageAlt}
      noImage={{ imageSize: "smallest", imageType: "user" }}
      ratio="1x1"
      src={profileImageUrl || ""}
    />
  );

  const image = profileImageUrl ? profileImage : instaImage;

  const onClickHandler = (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent> &
      React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    // For callback
    if (handleUserClick) {
      handleUserClick(e);
    }
  };

  return (
    <ContentAlignment
      alignment={"center"}
      justifyContent={"space-between"}
      className={styles.UserBar}
    >
      <ContentAlignment
        alignment={"center"}
        spacing={{ sm: "4", md: "4", lg: "4" }}
        className={styles.UserContent}
      >
        <Link
          element="a"
          href={personalPageLink || ""}
          target="_self"
          // isBlockElement
          onClick={onClickHandler}
          className={styles.UserLink}
        >
          <div
            className={
              profileImageUrl
                ? `${styles.UserImage}`
                : `${styles.UserImage} ${styles.UserInstaImage}`
            }
          >
            {image}
          </div>
        </Link>
        <ContentAlignment
          spacing={"4"}
          direction={{ sm: "column", md: "column", lg: "column" }}
          className={styles.UserInfo}
        >
          <Link
            element="a"
            href={personalPageLink || ""}
            target="_self"
            // isBlockElement
            onClick={onClickHandler}
            className={styles.UserLink}
          >
            <Caption
              element="p"
              color="primary-dark"
              className={styles.UserName}
            >
              {userName}
            </Caption>
          </Link>
          {modelHeight && (
            <Link
              element="a"
              href={personalPageLink || ""}
              target="_self"
              // isBlockElement
              onClick={onClickHandler}
              className={styles.UserLink}
            >
              <Caption
                element="p"
                color="primary-dark"
                className={styles.UserHeight}
              >
                {`${modelHeight || "-"}${t("text_style_model_height")}`}
              </Caption>
            </Link>
          )}
        </ContentAlignment>
      </ContentAlignment>
      {showWish && (
        <div className={styles.Toggle}>
          <Toggle
            variant="icon"
            icon="heart"
            count={count}
            toggled={toggled}
            onClick={handleWishClick}
          />
        </div>
      )}
    </ContentAlignment>
  );
};

export default UserBar;
